import { faChevronDown, faTimes, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Collapse, Divider, Grid, IconButton, Modal, Step, StepContent, Stepper, Typography } from '@mui/material';
import { Autocomplete, Checkbox, CheckboxGroup, ChosenThemeActionContext, Field, Form, FormsFeatureContext, ILookupItem, Numeric, RadioGroup, Text, ToggleButtonGroup, useFormLookups, useFormValidate, ValidationErrorType } from '@ngt/forms';
import { Condition, ConditionMode, IFormSubmit, ValidateOn } from '@ngt/forms-core';
import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { OtherInterests, PostApplication, ResearchApplication, ResponseStatus, Speciality } from './api/dtos';
import SpecialityArrayField from './components/AdditionalSpeciality';
import { ExpandMore } from './components/ExpandMore';
import FormButtons from './components/FormButtons';
import FormErrorDisplay from './components/FormErrorDisplay';
import StepLabelCheck from './components/StepLabelCheck';
import StepUpdater from './components/StepUpdater';
import Supporter from './components/Supporter';
import { FieldActionContext } from './contexts/FieldActionContext';
import { FieldContext } from './contexts/FieldContext';
import { StepContext } from './contexts/StepContext';
import ApplicationField from './fields/ApplicationField';

const initialValue = new ResearchApplication({additionalSpecialities: []});

const postSize = () => {
    window.parent.postMessage("scrolltop", "*")
};



const SpecialityItem = ({
    index, 
    onDelete
}: any) => {
    return (
        <ApplicationField
            label={`Additional Speciality ${(index + 1)}`}
            propertyPath={`[${index}]`}
            component={Text}
            margin="normal"
            sx={{
                innerWidth: "90%"
            }}
            InputProps={{
                endAdornment: (
                    <IconButton onClick={() => onDelete(index)}
                    size="small">
                        <FontAwesomeIcon icon={faTrash}/>
                    </IconButton>
                )
            }}
        />
    );
};

const Steps = [
    {
        label: 'Personal Details and Contact Information',
        Inputs: () => {
            return (
                <StepContext.Provider value={0}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <ApplicationField
                                label="Title"
                                propertyPath="title"
                                component={Autocomplete}
                                TextFieldProps={{
                                    margin: "normal",
                                }}
                            />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <ApplicationField
                                label="First Name"
                                propertyPath="firstName"
                                component={Text}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <ApplicationField
                                label="Last Name"
                                propertyPath="lastName"
                                component={Text}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ApplicationField
                                label="Institution/Company"
                                propertyPath="institution"
                                component={Text}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ApplicationField
                                label="Street Address"
                                propertyPath="address"
                                component={Text}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ApplicationField
                                label="Country"
                                propertyPath="country"
                                component={Autocomplete}
                                TextFieldProps={{
                                    margin: "normal"
                                }}
                            />
                        </Grid>
                        <Condition
                            condition={({value}) => value.country === 14}
                            subscription = {{
                                value: true
                            }}
                            mode={ConditionMode.Show}
                        >
                            <Grid item xs={12} md={6}>
                                <ApplicationField
                                    label="State"
                                    propertyPath="state"
                                    component={Autocomplete}
                                    TextFieldProps={{
                                        margin: "normal"
                                    }}
                                />
                            </Grid>
                        </Condition>
                        <Grid item xs={12} md={6}>
                            <ApplicationField
                                label="Suburb"
                                propertyPath="suburb"
                                component={Text}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ApplicationField
                                label="Post Code"
                                propertyPath="postcode"
                                component={Text}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ApplicationField
                                label="Business Phone"
                                propertyPath="phone"
                                component={Text}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ApplicationField
                                label="Business Fax Number"
                                propertyPath="fax"
                                component={Text}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ApplicationField
                                label="Mobile Number"
                                propertyPath="mobile"
                                component={Text}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ApplicationField
                                label="Email"
                                propertyPath="email"
                                component={Text}
                                margin="normal"
                                typeof='email'
                            />
                        </Grid>
                    </Grid>
                </StepContext.Provider>
            );
        }
    },
    {
        label: 'Specialities and Interests',
        Inputs: () => {
            return (
                <StepContext.Provider value={1}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography sx={{marginTop: "20px"}}>Please select your primary area of specialty from the list below (additional specialties and areas of interest can be added in the next section):</Typography>
                    </Grid>
                        <Grid item xs={12}>
                            <ApplicationField
                                label="Speciality"
                                propertyPath="speciality"
                                component={Autocomplete}
                                renderOption={(props: any, option: any) => {
                                    return (
                                        <li {...props}>
                                            <div>
                                                {option.value}
                                                {option.id === Speciality.Radiation_Oncologist && <div><small><em>Includes Radiation Therapist, Radiotherapist, Medical Physicist</em></small></div>}
                                                {option.id === Speciality.Study_Coordinator_or_Research_Nurse && <div><small><em>Includes Trial Coordinator, Data Manager, Nurse Counsellor, McGrath Care Nurse, Nurse Practitioner, Breast Screen, Clinical Nurse Consultant</em></small></div>}
                                                {option.id === Speciality.Consumer_Advocacy && <div><small><em>Including BCT CAP members</em></small></div>}
                                            </div>
                                        </li>
                                    )
                                }}
                                TextFieldProps={{
                                    margin: "normal"
                                }}
                            />
                        </Grid>
                        <Condition
                            condition={({value}) => value.speciality <=3}
                            subscription = {{
                                value: true
                            }}
                            mode={ConditionMode.Show}
                        >
                            <Grid item xs={12}>
                                <ApplicationField
                                    label="Please check if you are currently a Trainee or Fellow"
                                    propertyPath="trainee"
                                    component={Checkbox}
                                />
                            </Grid>
                        </Condition>
                        <Grid item xs={12}>
                            <Typography>Click “Additional Speciality” to enter up to 2 additional specialities - e.g. Medical oncologist who also specialises in Translational Research</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <SpecialityArrayField 
                                itemComponent={SpecialityItem}
                                noRecordTitle="No Specialities"
                                propertyPath="additionalSpecialities"
                                title="Additional Specialities"
                                titleVariant="h2"
                            />
                        </Grid>
                        <Grid item xs={8} marginTop="20px"><Typography>Do you have a higher degree?</Typography></Grid>
                        <Grid item xs={4} marginTop="20px">
                            <ApplicationField
                                label="Do you have a higher degree?"
                                propertyPath="higherDegree"
                                component={ToggleButtonGroup}
                                sx={{
                                    margin: "normal"
                                }}
                            />
                        </Grid>
                        <Condition
                            condition={({value}) => value.higherDegree}
                            subscription = {{
                                value: true
                            }}
                            mode={ConditionMode.Show}
                        >
                            <Grid item xs={12} md={6}>
                                <ApplicationField
                                    label="Type of Degree"
                                    propertyPath="degreeType"
                                    component={Text}
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={12} md={6} marginTop="16px" marginBottom="8px">
                                <ApplicationField
                                    label="Year Awarded"
                                    propertyPath="yearAwarded"
                                    component={Numeric}
                                    InputProps={{ 
                                        inputProps: { maxLength: 4 } 
                                    }}
                                />
                            </Grid>
                        </Condition>
                        <Grid item xs={8} marginTop="20px"><Typography>Are you interested in peer review of concepts/synopses/protocols?</Typography></Grid>
                        <Grid item xs={4} marginTop="20px">
                            <ApplicationField
                                label="Interested in peer review of concepts/synopses/protocols"
                                propertyPath="interestPeerReview"
                                component={ToggleButtonGroup}
                                sx={{
                                    margin: "normal"
                                }}
                            />
                        </Grid>
                        <Grid item xs={8} marginTop="20px"><Typography>Are you interested in being part of a Concept Development Working Group?</Typography></Grid>
                        <Grid item xs={4} marginTop="20px">
                            <ApplicationField
                                label="Interest in being part of a Concept Development Working Group"
                                propertyPath="interestConceptDevelopment"
                                component={ToggleButtonGroup}
                                sx={{
                                    margin: "normal"
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ApplicationField
                                label="Other areas of interest (check all that apply)"
                                propertyPath="otherInterests"
                                component={CheckboxGroup}
                                GridContainerProps={{
                                    spacing: 2
                                }}
                                GridItemProps={{
                                    xs: 6,
                                    md: 4,
                                    lg: 3
                                }}
                                sx={{
                                    margin: "normal"
                                }}
                            />
                            <Condition
                                condition={({value}) => value.otherInterests && 
                                    value.otherInterests.some((o: OtherInterests) => o === OtherInterests.Other) }
                                subscription = {{
                                    value: true
                                }}
                                mode={ConditionMode.Show}
                            >
                                <ApplicationField
                                    label="Specify"
                                    propertyPath="otherInterestSpecify"
                                    component={Text}
                                    margin="normal"
                                />
                            </Condition>
                        </Grid>
                    </Grid>
                </StepContext.Provider>
            );
        }
    },
    {
        label: 'Membership Request & Supporters',
        Inputs: () => {
            const [expandedA, setExpandedA] = React.useState(false);

            const handleExpandClickA = () => {
                setExpandedA(!expandedA);
            };

            const [expandedB, setExpandedB] = React.useState(false);

            const handleExpandClickB = () => {
                setExpandedB(!expandedB);
            };
            return (
                <StepContext.Provider value={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sx={{marginBottom: "-20px"}}>
                            <h4>I wish to be considered for membership of Breast Cancer Trials in the following category:</h4>
                        </Grid>
                        <Grid item xs={12} sx={{marginBottom: "-20px"}}>
                            <ApplicationField
                                label={null}
                                propertyPath="membershipTypeId"
                                component={RadioGroup}
                                getItemDescription={(item: ILookupItem, i: number) => {
                                    if (item.id === 1) {
                                        return <p>A member deemed by the Board who is or will be actively involved in the conduct of BCT's research program. Examples of Full Members include Trial Investigators in the surgical, medical and radiation oncology disciplines, Pathologists, basic Scientists, Trial Coordinators and other research staff. <span onClick={handleExpandClickA} className='benefits'>Full Member benefits.</span>
                                            <ExpandMore
                                                expand={expandedA}
                                                onClick={handleExpandClickA}
                                                aria-expanded={expandedA}
                                                aria-label="show more"
                                            >
                                                <FontAwesomeIcon icon={faChevronDown} color="#502d77"/>
                                            </ExpandMore>
                                            <Collapse in={expandedA} timeout="auto" unmountOnExit>
                                                <ul>
                                                    <li><em>Become actively involved in the BCT research program for the benefit of your patients and women generally. You will receive login access to the research section of the BCT website, where you can access important information such as clinical trial protocols and patient eligibility criteria (note that access to some trials on the website may be restricted).</em></li>
                                                    <li><em>Participate in the BCT's clinical trials research program. You may have the opportunity to become involved in BCT Committees, which set the BCT’s research agenda, and to seek discretionary funding to support research initiatives.</em></li>
                                                    <li><em>Nominate to be a BCT Board member. You also have all Company voting rights and can participate in the BCT Annual General Meeting.</em></li>
                                                    <li><em>Attend the Annual Scientific Meeting, which features leading national and international guest speakers and workshops, where the latest in clinical trials research and breast cancer research is presented and discussed.</em></li>
                                                </ul>
                                            </Collapse>
                                        </p>
                                    }
                                    return <p>
                                                A member deemed by the Board to have an interest in, and awareness of, the BCT and its research activities but who is not actively involved in the conduct of BCT's research program. <span onClick={handleExpandClickB} className='benefits'>Affiliate Member benefits.</span>
                                        <ExpandMore
                                            expand={expandedB}
                                            onClick={handleExpandClickB}
                                            aria-expanded={expandedB}
                                            aria-label="show more"
                                        >
                                            <FontAwesomeIcon icon={faChevronDown} color="#502d77"/>
                                        </ExpandMore>
                                        <Collapse in={expandedB} timeout="auto" unmountOnExit>
                                            <ul>
                                                <li><em>Membership correspondence, including updates on BCT's research, fundraising activities and the Annual Report.</em></li>
                                                <li><em>Invitation to attend the Annual Scientific Meeting, which features leading national and international guest speakers and workshops, where the latest in clinical trial research and breast cancer research is presented and discussed.</em></li>
                                            </ul>
                                        </Collapse>
                                    </p>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{marginBottom: "-20px"}}>
                            <h4>As part of your application, you need to nominate a proposer and a seconder, who are current members of Breast Cancer Trials. In the space below, enter the FIRSTNAME SURNAME to confirm that they are a full member of Breast Cancer Trials.</h4>
                        </Grid>
                        <Grid item xs={12}>
                            <ApplicationField
                                label="I don't know any existing members of Breast Cancer Trials."
                                propertyPath="noSupporters"
                                component={Checkbox}
                            />
                        </Grid>
                        <Condition
                            condition={({value}) => !value.noSupporters}
                            subscription = {{
                                value: true
                            }}
                            mode={ConditionMode.Show}
                        >
                            <Grid item xs={12}>
                                <Grid item xs={12}>
                                <Supporter propertyPath="proposer" label="Proposer"/>
                                    <Field propertyPath="proposerId" />
                                </Grid>
                                <Grid item xs={12}>
                                <Supporter propertyPath="seconder" label="Seconder"/>
                                    <Field propertyPath="seconderId" />
                                </Grid>
                            </Grid>
                        </Condition>
                        <Grid item xs={12}>
                            <ApplicationField
                                multiline={true}
                                rows={5}
                                label="Applicant's current involvement in BCT activities"
                                propertyPath="involvement"
                                component={Text}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ApplicationField
                                multiline={true}
                                rows={5}
                                label="Why do you wish to become a member of BCT"
                                propertyPath="reason"
                                component={Text}
                                margin="normal"
                            />
                        </Grid>
                    </Grid>
                </StepContext.Provider>
            );
        }
    },
    {
        label: 'Membership Confirmation',
        Inputs: () => {
            return (
                <StepContext.Provider value={3}>
                    <Grid container spacing={2}>
                        <Grid item xs={6} md={8} sx={{marginTop: "18px"}}>
                            <Typography>Do you agree to BCT providing your email address to other Group members and associated collaborative research groups if requested</Typography>
                        </Grid>
                        <Grid item xs={6} md={4} marginTop="20px">
                            <ApplicationField
                                label=""
                                propertyPath="emailPermission"
                                component={ToggleButtonGroup}
                            />
                        </Grid>
                        <Grid item xs={6} md={8}sx={{marginTop: "18px"}}>
                            <Typography>Do you agree to your contact details being included in the Members Only access section of BCTs website</Typography>
                        </Grid>
                        <Grid item xs={6} md={4} marginTop="20px">
                            <ApplicationField
                                label=""
                                propertyPath="websitePermission"
                                component={ToggleButtonGroup}
                            />
                        </Grid>
                        <Grid 
                            item 
                            xs={12}
                        >
                            <ApplicationField
                                label="I hereby apply for membership of the Breast Cancer Trials, and in doing so agree to uphold the ideals of BCT and promote its objectives through my collaboration and support of the clinical trials conducted by BCT"
                                propertyPath="idealsConsent"
                                component={Checkbox}
                            />
                        </Grid>
                        <Grid 
                            item 
                            xs={12}
                        >
                            <ApplicationField
                                label="I consent to my details being recorded in the register of members of BCT and I agree to abide by BCT's constitution as varied from time to time including the provision that requires me to contribute $10 to the assets of BCT in the event that BCT is wound up"
                                propertyPath="constitutionConsent"
                                component={Checkbox}
                                FormControlLabelProps={{
                                    sx: {
                                        alignItems: 'flex-start',
                                        "& > *:nth-of-type(2)": {
                                            pt: '9px'
                                        }
                                    }
                                } as any}
                            />
                        </Grid>
                        <Grid 
                            item 
                            xs={12}
                            sx={{
                                marginBottom: "20px"
                            }}
                        >
                            <ApplicationField
                                label="I agree that this application is governed by the law in force in New South Wales and I submit to the non-exclusive jurisdiction of the courts of or exercising jurisdiction in New South Wales"
                                propertyPath="lawAcknowledgement"
                                component={Checkbox}
                            />
                        </Grid>
                        <Grid 
                            item 
                            xs={12}
                        >
                            <FormErrorDisplay />
                            <Field
                                propertyPath=''
                            />
                        </Grid>
                    </Grid>
                </StepContext.Provider>
            );
        }
    }
];


const App: FC = () => {
    const setChosenTheme = useContext(ChosenThemeActionContext);
    const { serviceStackClient } = useContext(FormsFeatureContext);
    const {data:lookups} = useFormLookups(1, { revalidateOnFocus: false, refreshWhenOffline: false, })
    const validate: any = useFormValidate(1);
    useEffect(() => {
        setChosenTheme('light');
    }, [])

    const [open, setOpen] = React.useState(true);
    const handleClose = () => setOpen(false);
    const [activeStep, setActiveStep] = React.useState(0);
    const [completed, setCompleted] = React.useState<Record<number, boolean>>({});

    const totalSteps = () => {
        return Steps.length;
    };

    const completedSteps = useCallback(() => {
        const keys = Object.keys(completed);

        for (let i = 0; i < keys.length; i++) {
            if (!completed[+keys[i]]) {
                return i;
            } 
        }

        return keys.length;
    }, [completed]);


  
    const stepLabelOnClick = useCallback((index: number) => {
        const finishedSteps = completedSteps();

        if (finishedSteps >= index) {
            setActiveStep(index);
        }
        else {
            setActiveStep(finishedSteps);
        }

        postSize();
    }, [completedSteps, setActiveStep, postSize]);

    const isLastStep = () => {
        return activeStep === totalSteps() - 1;
    };

    const allStepsCompleted = () => {
        return completedSteps() === totalSteps();
    };

    const handleNext = () => {
        const newActiveStep =
            isLastStep() && 
            !allStepsCompleted() ? 
                // It's the last step, but not all steps have been completed,
                // find the first step that has been completed
                Steps.findIndex((_step, i) => !(i in completed)) :
                activeStep + 1;
        setActiveStep(newActiveStep);
        postSize();
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
        postSize();
    };

    const onSubmit: IFormSubmit<ResearchApplication> = React.useCallback(async (formState, actions) => {
        try {
            const response = await serviceStackClient.post(new PostApplication({
                application: formState.value!
            }));

            setActiveStep(4);
            postSize();
            actions.reset();
        }
        catch (error: any) {

            if (error?.responseStatus) {
                const responseStatus: ResponseStatus = error?.responseStatus;

                return {
                    '': [
                        {  
                            property: '',
                            message: responseStatus.message,
                            type: ValidationErrorType.Normal
                        }
                    ]
                }
            }

            return {
                '': [
                    {  
                        property: '',
                        message: 'An error occurred while attempting to save the application.',
                        type: ValidationErrorType.Normal
                    }
                ]
            }
        }
    }, [serviceStackClient, postSize, setActiveStep]);



    return (
        <BrowserRouter>
            <Form
                initialValue={initialValue}
                debug={import.meta.env.DEV}
                lookups={lookups}
                onValidate={validate}
                validateOn={ValidateOn.onChange}
                onSubmit={onSubmit}
            >
                <StepUpdater setCompleted={setCompleted} />
                <Typography variant="h1" marginTop="20px" marginBottom="10px">
                    Research Member Application
                </Typography>
                <Divider />
                <Stepper nonLinear sx={{marginTop: "20px", marginBottom: "20px"}} activeStep={activeStep} orientation="vertical">
                    {Steps.map(({label, Inputs}, index) => (
                            <Step key={label} completed={completed[index]}>
                                <StepLabelCheck 
                                    onClick={() => stepLabelOnClick(index)}
                                    label={label}
                                    index={activeStep}
                                    setComplete={setCompleted}
                                />
                                <StepContent>
                                    <Inputs />
                                    <FormButtons 
                                        activeStep={activeStep}
                                        handleBack={handleBack}
                                        handleNext={handleNext}
                                        setCompleted={setCompleted}
                                    />
                                </StepContent>
                            </Step>
                    ))}
                </Stepper>
            </Form>
            {                     
                activeStep === 4 ?
                    <div>
                        <Modal
                            open={open}
                            onClose={handleClose}
                            aria-labelledby="modal-modal-title"
                            aria-describedby="modal-modal-description"
                        >
                        <Box sx={{
                                position: 'absolute' as 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 400,
                                bgcolor: 'background.paper',
                                boxShadow: 24,
                                p: 4,
                            }}
                        >
                            <IconButton 
                                color='primary'
                                onClick={handleClose}
                                sx={{
                                    float: "right"
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </IconButton>
                            <Typography 
                                id="modal-modal-title" 
                                variant="h6" 
                                component="h2"
                                sx={{
                                    marginTop: "10px"
                                }}
                            >
                                Success!
                            </Typography>
                            <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                                Congratulations, your application has been submitted and you will hear from us shortly.
                            </Typography>
                        </Box>
                        </Modal>
                    </div> :
                    null
            }
        </BrowserRouter>
    )
}

const WrappedApp = () => {
    const [fields, setFields] = useState<Record<number,string[]>>({});

    return (
        <FieldContext.Provider value={fields}>
            <FieldActionContext.Provider value={setFields}>
                <App/>
            </FieldActionContext.Provider>
        </FieldContext.Provider>
    );
}

export default WrappedApp;